<script lang="ts">
    import type {TabButton, TabsetId} from 'shared/ui-widgets/tabset/types';
    import {Tab, TabGroup, TabList} from '@rgossiaux/svelte-headlessui';
    import {exists, isFunction} from 'shared/utils/custom-utils';
    import {getInjector} from 'src/core';
    import {map} from 'rxjs/operators';
    import KpTabGroupChangeListener from 'shared/ui-widgets/tabset/KpTabGroupChangeListener.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import KpTabButtonsActiveTabSelector from 'shared/ui-widgets/tabset/KpTabButtonsActiveTabSelector.svelte';
    import {ErpFondPermissionsContextService, type FondPermissions} from 'src/features/erp';

    export let tabButtons: TabButton[];
    export let tabToSelect: TabsetId | null = null;
    export let hrefGenerator: ((id: TabsetId) => string) | null = null;
    export let fillWidth = false;

    const fondPermissionsService = getInjector().getByClass(ErpFondPermissionsContextService);
    const filteredButtons$ = fondPermissionsService.fondPermissions$.pipe(map((permissions) => filterTabButtonsByPermissions(permissions, tabButtons)));

    function filterTabButtonsByPermissions(permissions: FondPermissions, buttons: TabButton[]): TabButton[] {
        return buttons.filter((button) => {
            return fondPermissionsService.hasFondPermissions(permissions, button.showPermissionFond, button.editPermissionFond);
        });
    }
</script>

<TabGroup class="kp-button-tabs {fillWidth ? 'fill-width' : ''}">
    <KpTabGroupChangeListener on:tab-change/>
    <KpTabButtonsActiveTabSelector {tabToSelect}/>

    <TabList class="kp-button-tabs-list">
        {#each $filteredButtons$ as tab(tab.id)}
            <Tab role="tab"
                 as={isFunction(hrefGenerator) ? 'a' : 'button'}
                 data-tab={tab.id}
                 disabled={tab.disabled}
                 class={({selected}) => `kp-button-tabs-item no-decoration-anchor ${tab.id} ${selected ? 'selected' : ''}`}
                 {...(isFunction(hrefGenerator) ? {href: hrefGenerator(tab.id)} : {})}>

                {#if !exists($$slots.customTab)}
                    <IconedContent icon={tab.icon}>
                        {tab.label}
                    </IconedContent>
                {:else}
                    <slot name="customTab" {tab}/>
                {/if}
            </Tab>
        {/each}
    </TabList>
</TabGroup>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro.themes.less";

    :global {
        .kp-button-tabs {
            &.fill-width {
                width: 100%;

                .kp-button-tabs-list .kp-button-tabs-item {
                    flex: 1;
                }
            }

            .kp-button-tabs-list {
                display: flex;
                gap: @spacing-s;
                flex-wrap: wrap;

                .kp-button-tabs-item {
                    padding: @spacing-s @spacing-m;
                    background-color: @themed-body-bg;
                    border: 1px solid @themed-border-default;
                    color: @themed-text-default;
                    white-space: nowrap;
                    outline: none;
                    cursor: pointer;
                    border-radius: @border-radius-default;
                    transition: background-color 0.2s ease-in-out, border-color 0.2s ease-in-out, opacity 0.2s ease-in-out, color 0.2s ease-in-out;

                    &.selected {
                        background-color: var(--selected-tab-highlight-color);
                        border: 1px solid rgba(0, 0, 0, 0.2);
                        color: white;
                    }

                    &:disabled {
                        opacity: 0.5;
                    }

                    &:enabled:not(.selected) {
                        &:hover {
                            background-color: @themed-panel-bg;
                        }
                    }
                }
            }
        }
    }
</style>